import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { FileUploader } from "react-drag-drop-files";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Swal from "sweetalert2";
import { useApiStore } from "../../hooks/ApiStoreContext";

const fileTypes = ["JPEG", "JPG", "PNG"];

function AddCategory() {
  const apiStore = useApiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [heading, setHeading] = useState("Add");
  const [parentCategories, setParentCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [fileChange, setFileChange] = useState(false);

  const handleFileChange = (file) => {
    setFile(file);
    setFileChange(true);
  };

  const handleFileDownload = () => {
    if (file) {
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      categoryId: 0,
      name: "",
      link: "1",
      imageSource: "",
      description: "",
      parentCategoryId: null,
      isActive: true,
    },
  });

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/category");
  };

  const fetchParentCategories = async (currentCategoryId) => {
    try {
      let response = await apiStore.get("/Categories/GetAllActiveCategories");

      if (currentCategoryId) {
        // Remove currentCategoryId and its descendants from Parent Category Id
        const descendants = getDescendants(response, currentCategoryId);
        response = response.filter((c) => !descendants.includes(c.categoryId) && c.categoryId != currentCategoryId);
      }
      setParentCategories(response);
    } catch (error) {
      if (error.response.status !== 401) {
        console.error("There was an error fetching parent categories!", error);
      }
    }
  };

  const getDescendants = (categories, categoryId, descendants = []) => {
    categories.forEach((category) => {
      if (category.parentCategoryId == categoryId) {
        descendants.push(category.categoryId);
        getDescendants(categories, category.categoryId, descendants);
      }
    });
    return descendants;
  };

  useEffect(() => {
    const id = location.pathname.split("/")[2];
    const fetchParentCategoriesAsync = async () => {
      await fetchParentCategories(id);
      if (id) {
        fetchCategoryData(id);
      }
    };
    fetchParentCategoriesAsync();

    if (!id) {
      setHeading("Add");
    } else {
      setHeading("Edit");
      setValue("categoryId", id);
    }
  }, [location]);

  const fetchCategoryData = async (id) => {
    try {
      const response = await apiStore.get(`/categories/${id}`);
      setValue("name", response.name);
      setValue("link", response.link);
      setValue("description", response.description);
      setValue("imageSource", response.imageSource);
      setValue("parentCategoryId", response.parentCategoryId);
      setValue("isActive", response.isActive);

      const imageBlob = await apiStore.getBlob(`/categories/getImage/${id}`);
      const filename = response.imageSource.split("/").pop();
      const type = imageBlob.type;

      const fetchedImageFile = new File([imageBlob], filename, { type });
      setFile(fetchedImageFile);
    } catch (error) {
      console.error("There was an error fetching the category!", error);
    }
  };

  const onSubmit = async (data) => {
    if (!file) {
      await Swal.fire({ icon: "warning", title: "Oops...", text: "Please upload an Image before submitting." });
      return;
    }

    try {
      if (fileChange) {
        const formData = new FormData();
        formData.append("image", file);

        const addImageResponse = await apiStore.postFormData("/categories/addImage", formData);
        if (!!data.imageSource && !!data.categoryId) {
          try {
            await apiStore.delete(`/categories/deleteImage/${data.categoryId}`);
          } catch (error) {
            console.error("There was an error deleting the existing category image!", error);
          }
        }

        data.imageSource = addImageResponse.imageSource;
      }

      if (!data.parentCategoryId) {
        data.parentCategoryId = null;
      } else {
        data.parentCategoryId = Number(data.parentCategoryId);
      }

      if (!data.categoryId) {
        await apiStore.post("/categories", data);
        await Swal.fire({ title: "Success", text: "Category saved successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await apiStore.put(`/categories/${data.categoryId}`, data);
        await Swal.fire({ title: "Updated", text: "Category updated successfully!", icon: "success", confirmButtonText: "OK" });
      }

      navigate("/category");
    } catch (error) {
      if (error.response.status !== 401) {
        console.error("There was an error saving the category!", error);
        Swal.fire({ title: "Error", text: "There was an error saving the category.", icon: "error", confirmButtonText: "OK" });
      }
    }
  };

  const handleCancel = (e) => {
    reset({
      name: "",
      link: "",
      description: "",
      parentCategoryId: null,
      isActive: true,
    });
    setFile(null);
    navigate("/category");
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <CardHeader className="pb-0">
            <Row>
              <Col md={9}>
                <h4>{heading} Category Details</h4>
              </Col>
              <Col md={3} className="d-grid d-md-flex justify-content-md-end d-sm-block">
                <Button variant="outline-primary" className="btn-sm" onClick={handleBack}>
                  Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="px-0 pt-0 pb-2">
            <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
              <Row className="mb-3">
                <input type="hidden" {...register("categoryId")}></input>
                <Form.Group controlId="name" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Category Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Category name" {...register("name", { required: "Category name is required." })} />
                  <span className="text-xs text-danger">{errors.name?.message}</span>
                </Form.Group>
                <Form.Group controlId="parentCategoryId" className="col-md-6 col-sm-12">
                  <Form.Label>Parent Category</Form.Label>
                  <Form.Select {...register("parentCategoryId")}>
                    <option value="">Select Parent Category</option>
                    {parentCategories.map((parentCategory) => (
                      <option key={parentCategory.categoryId} value={parentCategory.categoryId}>
                        {parentCategory.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="description" className="col-md-6 col-sm-12">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" rows={3} placeholder="Description" {...register("description", { maxLength: { value: 500, message: "Maximum 500 characters allowed." } })} />
                  <span className="text-xs text-danger">{errors.description?.message}</span>
                </Form.Group>
                {/* <Form.Group controlId="image" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Image <span className="text-danger">*</span>
                  </Form.Label>
                  <FileUploader multiple={false} handleChange={handleFileChange} name="file" types={fileTypes} fileOrFiles={file ? [file] : null} />
                  <p>
                    {file ? (
                      <>
                        {file.name} <FontAwesomeIcon icon={faDownload} onClick={handleFileDownload} style={{ cursor: "pointer" }} />
                      </>
                    ) : (
                      "No file uploaded yet"
                    )}
                  </p>
                </Form.Group> */}
                <Form.Group controlId="image" className="col-md-6 col-sm-12">
                <Form.Label>
                  Image <span className="text-danger">*</span>
                </Form.Label>
                <FileUploader
                  multiple={false}
                  handleChange={handleFileChange}
                  name="file"
                  types={fileTypes}
                  fileOrFiles={file ? [file] : null}
                />
                {file ? (
                  <div>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="thumbnail"
                      style={{ width: "100px", height: "100px", objectFit: "cover" }}
                    />
                    <p>
                      {file.name}{" "}
                      <FontAwesomeIcon
                        icon={faDownload}
                        onClick={handleFileDownload}
                        style={{ cursor: "pointer" }}
                      />
                    </p>
                  </div>
                ) : (
                  <p>No file uploaded yet</p>
                )}
              </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="isActive" className="col-md-3 col-sm-12">
                  <Form.Label>Is Active</Form.Label>
                  <Form.Check type="switch" {...register("isActive")} />
                </Form.Group>
              </Row>
              <Row>
                <Col md={12} className="d-grid gap-2 d-md-flex flex-md-row-reverse">
                  <Button variant="primary" className="me-md-2" type="submit">
                    Submit
                  </Button>
                  <Button variant="dark" type="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

export default AddCategory;
